import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertCircle, CheckCircle2, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PortalVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verificationStep, setVerificationStep] = useState('checking'); // checking, claim, verify-npn, success, error
  const [userInfo, setUserInfo] = useState(null);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [showRegistrationPreview, setShowRegistrationPreview] = useState(false);
  const [isAgent, setIsAgent] = useState(null); // null = not answered, true/false = answered
  const [npn, setNpn] = useState('');
  const [verifyingNPN, setVerifyingNPN] = useState(false);
  const [verifiedAgentData, setVerifiedAgentData] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');

  const locationId = searchParams.get('location_id');
  const userId = searchParams.get('user_id');

  useEffect(() => {
    verifyAccess();
  }, []);

  const verifyAccess = async () => {
    try {
      // Step 1: Check URL parameters
      if (!locationId || !userId) {
        setError('Missing required parameters. Please open this portal through Simplywork.');
        setVerificationStep('error');
        setLoading(false);
        return;
      }

      // Step 2: Check if user exists
      const response = await axios.post(`${API}/auth/check-user`, {
        userId: userId
      });

      if (response.data.found === false || !response.data.userData) {
        // User not found, show claim dialog
        setVerificationStep('claim');
        setShowClaimDialog(true);
        setLoading(false);
      } else {
        // User exists, extract userData and store
        const userData = response.data.userData;
        const agentInfo = userData.agent_info || {};
        
        // Check for Commission permission
        const permissions = userData.meta_ghl_user_permission_id || [];
        const hasCommissionPermission = permissions.some(
          permission => permission.user_permission?.permission === 'Commission'
        );
        
        // Extract FFL agency access
        const agencyAccess = (userData.agent_ffl_agency_id || []).map(
          item => item.ffl_agency_access
        ).filter(Boolean);
        
        const userInfoToStore = {
          user_id: userId,
          agent_id: userData.agent_id,
          location_id: locationId,
          first_name: userData.first_name,
          last_name: userData.last_name,
          ghl_userid: userData.ghl_userid,
          name: `${userData.first_name} ${userData.last_name}`,
          email: agentInfo.email || null,
          npn: agentInfo.npn || null,
          status: agentInfo.status || null,
          permissions: permissions,
          hasCommissionPermission: hasCommissionPermission,
          agent_ffl_agency_access: agencyAccess,
          // Agent Info details
          fflAgency_id: agentInfo.fflAgency_id || null,
          fflAgency_name: agentInfo.fflAgency_name || null,
          agentUpline_id: agentInfo.agentUpline_id || null,
          agentUpline_name: agentInfo.agentUpline_name || null,
          agentUpline_npn: agentInfo.agentUpline_npn || null,
          agent_status: agentInfo.status || null,
          // GHL Location Info
          ghlLocation_id: agentInfo.ghlLocation_id || null,
          ghlLocation_name: agentInfo.ghlLocation_name || null
        };
        
        setUserInfo(userInfoToStore);
        localStorage.setItem('user_info', JSON.stringify(userInfoToStore));
        localStorage.setItem('location_id', locationId);
        setVerificationStep('success');
        setLoading(false);
        
        // Redirect to portal after a short delay
        setTimeout(() => {
          navigate('/portal');
        }, 1500);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Failed to verify access. Please try again.');
      setVerificationStep('error');
      setLoading(false);
    }
  };

  const handleVerifyNPN = async () => {
    if (!npn || npn.length < 5) {
      toast.error('Please enter a valid NPN.');
      return;
    }

    setVerifyingNPN(true);

    try {
      // Verify NPN
      const verifyResponse = await axios.post(`${API}/auth/verify-npn`, {
        npn: npn
      });

      if (verifyResponse.data.status !== 'active') {
        toast.error('Your NPN is not active. Please contact support.');
        setVerifyingNPN(false);
        return;
      }

      // NPN is valid, show preview with first name, last name, and NPN
      setVerifiedAgentData({
        first_name: verifyResponse.data.first_name,
        last_name: verifyResponse.data.last_name,
        npn: npn
      });
      setVerifyingNPN(false);
      toast.success('NPN verified successfully!');
    } catch (err) {
      console.error('NPN verification error:', err);
      toast.error('Failed to verify NPN. Please try again.');
      setVerifyingNPN(false);
    }
  };

  const handleProceed = async () => {
    setRegistering(true);
    
    try {
      // Call create user API
      const payload = {
        user_id: userId,
        npn: isAgent && verifiedAgentData ? npn : null
      };

      const response = await axios.post(`${API}/portal/ghl-user/create`, payload);
      
      if (response.data.success) {
        toast.success('User created successfully! Verifying...');
        
        // Close the claim dialog
        setShowClaimDialog(false);
        
        // Reload the page with the same parameters to re-run verification
        // This will now find the user in the database and fetch complete user data
        setTimeout(() => {
          window.location.href = `/?location_id=${locationId}&user_id=${userId}`;
        }, 1000);
      }
    } catch (err) {
      console.error('User creation error:', err);
      toast.error('Failed to create user. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fafaf9 0%, #f5f5f4 100%)' }}>
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <Shield className="w-16 h-16 text-[#d4af37] animate-pulse" />
          </div>
          <h2 className="text-2xl font-semibold text-neutral-800">Verifying Access...</h2>
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-[#d4af37]" />
            <p className="text-neutral-600">Please wait while we verify your credentials</p>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStep === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fafaf9 0%, #f5f5f4 100%)' }}>
        <Card className="w-full max-w-md border-red-200 shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="w-16 h-16 text-red-500" />
            </div>
            <CardTitle className="text-2xl text-red-600">Access Denied</CardTitle>
            <CardDescription className="text-neutral-600 mt-2">{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (verificationStep === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fafaf9 0%, #f5f5f4 100%)' }}>
        <Card className="w-full max-w-md border-[#d4af37] shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-[#d4af37]" />
            </div>
            <CardTitle className="text-2xl text-neutral-800">Verification Successful</CardTitle>
            <CardDescription className="text-neutral-600 mt-2">
              Redirecting to your portal...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fafaf9 0%, #f5f5f4 100%)' }}>
      {/* Claim User Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent className="sm:max-w-md" data-testid="claim-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Claim User ID</DialogTitle>
            <DialogDescription>
              This user ID is not registered. Please complete the following steps.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Step 1: Are you an agent? */}
            {isAgent === null && (
              <div className="space-y-4">
                <Label className="text-base font-semibold">Are you an agent?</Label>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setIsAgent(true)}
                    variant="outline"
                    className="flex-1 border-[#d4af37] hover:bg-[#f4e5b8]"
                  >
                    Yes
                  </Button>
                  <Button
                    onClick={() => setIsAgent(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    No
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: If agent, verify NPN */}
            {isAgent === true && !verifiedAgentData && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Verify Your NPN</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsAgent(null);
                      setNpn('');
                    }}
                    className="text-xs"
                  >
                    Change Answer
                  </Button>
                </div>
                <Label htmlFor="npn" className="text-sm">National Producer Number (NPN)</Label>
                <Input
                  id="npn"
                  placeholder="Enter your NPN"
                  value={npn}
                  onChange={(e) => setNpn(e.target.value)}
                  data-testid="npn-input"
                />
                <Button
                  onClick={handleVerifyNPN}
                  disabled={!npn || verifyingNPN}
                  className="w-full bg-[#d4af37] hover:bg-[#b8941e] text-white"
                >
                  {verifyingNPN ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify NPN'
                  )}
                </Button>
              </div>
            )}

            {/* Step 3: Show verified agent preview */}
            {isAgent === true && verifiedAgentData && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-green-600">✓ NPN Verified</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setVerifiedAgentData(null);
                      setNpn('');
                    }}
                    className="text-xs"
                  >
                    Edit NPN
                  </Button>
                </div>
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-neutral-600">First Name</p>
                      <p className="font-medium text-neutral-800">{verifiedAgentData.first_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-600">Last Name</p>
                      <p className="font-medium text-neutral-800">{verifiedAgentData.last_name}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-600">NPN</p>
                    <p className="font-medium text-neutral-800">{verifiedAgentData.npn}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Non-agent confirmation */}
            {isAgent === false && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Non-Agent Access</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsAgent(null)}
                    className="text-xs"
                  >
                    Change Answer
                  </Button>
                </div>
                <div className="bg-neutral-50 border border-neutral-200 p-4 rounded-lg">
                  <p className="text-sm text-neutral-600">
                    You will be registered as a non-agent user.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            {(isAgent === true && verifiedAgentData) || isAgent === false ? (
              <Button
                onClick={handleProceed}
                disabled={registering}
                className="w-full bg-[#d4af37] hover:bg-[#b8941e] text-white"
              >
                {registering ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  'Proceed to Portal'
                )}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}